import { type ReactNode, useEffect, useLayoutEffect, useState } from "react";
import { type Failure, failureWords, type Opened } from "./failure.ts";
import { ReportThis } from "./ReportThis.tsx";
import { Button } from "./souvenir.ts";
import { Message } from "./ui.tsx";

// Opening a page (features/opening.md). The sunrise is drawn by the page itself (index.html) from its first
// paint, outside #root, so it plays while the scripts arrive; here the app takes it over without it starting
// again (no second copy: React never draws it), keeps it while what the page shows is still loading, and
// fades it into the page once that's ready. A page that's still loading says so by rendering <Opening />;
// once no page does, the sunrise fades out and is taken out of the page, so it never sits under it or
// catches a tap. A page opened later inside the app (a trip from Your trips) gets a fresh copy of it, which
// fades in after a moment, so a page that's ready at once never blinks it.

// The sunrise, and how its words are stopped (index.html's `tripmoOpening` runs them).
type Sunrise = HTMLElement & { tripmoStop?: (() => void) | undefined };

declare global {
  interface Window {
    tripmoOpening?: (sunrise: HTMLElement, boot: boolean) => () => void;
  }
}

// The sunrise's fade into the page (index.html, `.tm-opening`'s transition), with a moment to spare.
const FADE_MS = 360;

const boot = document.getElementById("opening") as Sunrise | null;
// For a page opened inside the app: the sunrise as the page drew it, before any of its words turned.
const template = boot ? fresh(boot) : null;
// The sunrise on screen, if any, and how many pages are still loading under it.
let shown: Sunrise | null = boot;
let loading = 0;

function fresh(from: HTMLElement): HTMLElement {
  const copy = from.cloneNode(true) as HTMLElement;
  copy.removeAttribute("id");
  copy.className = "tm-opening is-copy";
  for (const [i, line] of copy.querySelectorAll(".tm-opening-line").entries())
    line.className = i === 0 ? "tm-opening-line is-on" : "tm-opening-line";
  return copy;
}

// The app's scripts have arrived: the sunrise is the app's now. It never says they didn't (and stops
// saying so, if they came after it did).
export function takeOver() {
  if (!boot) return;
  boot.classList.remove("is-stuck");
  boot.classList.add("is-app");
  performance.mark("tripmo:app");
}

// Rendered by a page while what it shows is still loading: the sunrise stays (or, inside the app, comes).
export function Opening() {
  useLayoutEffect(() => {
    hold();
    return release;
  }, []);
  return null;
}

function hold() {
  loading += 1;
  if (shown) {
    // Fading out as another page starts loading: it stays after all.
    shown.classList.remove("is-done");
    return;
  }
  if (!template) return;
  const copy = template.cloneNode(true) as Sunrise;
  document.body.append(copy);
  copy.tripmoStop = window.tripmoOpening?.(copy, false);
  shown = copy;
}

// Once the commit that let go of it is done (the next page, rendered with it, may still be loading).
function release() {
  loading -= 1;
  queueMicrotask(settle);
}

// No page loading any more: the sunrise fades into what the page shows, and goes.
export function settle() {
  if (loading > 0) return;
  const sunrise = shown;
  if (!sunrise || sunrise.classList.contains("is-done")) return;
  performance.mark("tripmo:opened");
  sunrise.classList.add("is-done");
  const gone = () => {
    if (!sunrise.classList.contains("is-done") || !sunrise.isConnected) return;
    sunrise.tripmoStop?.();
    sunrise.remove();
    if (shown === sunrise) shown = null;
  };
  sunrise.addEventListener("transitionend", (e) => e.target === sunrise && gone());
  setTimeout(gone, FADE_MS + 140);
}

// Opening failed: why, in plain words, with Try again, which loads what failed again (`again`) and says so
// until its answer comes (`retrying`). A trip also offers the way back to Your trips; and, Tripmo not answering,
// Report this, the feedback form with the request that failed (features/feedback.md).
export function Failed({
  failure,
  what,
  again,
  retrying,
  back,
}: {
  failure: Failure;
  what: Opened;
  again: () => void;
  retrying: boolean;
  back?: ReactNode;
}) {
  const { title, body } = failureWords(failure, what, device());
  return (
    <Message
      title={title}
      body={body}
      action={
        <div className="tm-message-actions">
          <TryAgain again={again} retrying={retrying} />
          {back}
          {failure === "not-answering" && <ReportThis line always />}
        </div>
      }
    />
  );
}

// Your trips' own: the page stays (its bar, the way to the profile), the words where its trips go.
export function TripsFailed({ failure, again, retrying }: { failure: Failure; again: () => void; retrying: boolean }) {
  const { title, body } = failureWords(failure, "trips", device());
  return (
    <section className="tm-trips-failed" aria-labelledby="trips-failed">
      <h2 id="trips-failed" className="title">
        {title}
      </h2>
      <p className="body tm-muted">{body}</p>
      <TryAgain again={again} retrying={retrying} />
      {failure === "not-answering" && <ReportThis line always />}
    </section>
  );
}

// "Trying again…" for at least a moment, so a try that fails again at once (still no signal) is seen to
// have been made.
const TRYING_AT_LEAST_MS = 700;

function TryAgain({ again, retrying }: { again: () => void; retrying: boolean }) {
  const [tapped, setTapped] = useState(false);
  useEffect(() => {
    if (!tapped) return;
    const done = setTimeout(() => setTapped(false), TRYING_AT_LEAST_MS);
    return () => clearTimeout(done);
  }, [tapped]);
  const trying = tapped || retrying;
  return (
    <Button
      variant="primary"
      size="sm"
      className="tm-try-again"
      disabled={trying}
      onClick={() => {
        setTapped(true);
        again();
      }}
    >
      {trying ? "Trying again…" : "Try again"}
    </Button>
  );
}

// What this device is called in what a page says: a phone, or a computer.
export const device = () => (matchMedia("(pointer: coarse)").matches ? "phone" : "computer");
